import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules';

import type { TutorDocument } from '../../../types/tutorAi';
import { ConfirmDialog } from '../../molecules/ConfirmDialog';
import { ContextFileRow } from '../../molecules/ContextFileRow';
import { SurfaceCard } from '../../molecules/SurfaceCard';

export interface ContextFileListProps {
  readonly documents: readonly TutorDocument[];
  readonly isLoading: boolean;
  readonly onImport: (documentId: number) => void;
  readonly onDelete: (documentId: number) => void;
}

/**
 * Lista dos documentos de contexto de uma turma.
 *
 * A exclusão passa por confirmação: tira o material do contexto do tutor para todos os
 * alunos da turma.
 */
export function ContextFileList({
  documents,
  isLoading,
  onImport,
  onDelete,
}: ContextFileListProps) {
  const { t } = useTranslation('tutorAI');
  const [pending, setPending] = useState<TutorDocument | null>(null);

  function handleConfirm() {
    if (!pending) return;
    onDelete(pending.id);
    setPending(null);
  }

  return (
    <>
      <SurfaceCard
        title={t('classDetail.documents.sectionTitle')}
        headerRight={
          <span className="text-ctx-content-placeholder ts-caption-xxs">
            {t('classDetail.documents.count', { count: documents.length })}
          </span>
        }
      >
        {isLoading ? (
          <div className="flex flex-col gap-mini p-xxs">
            <Skeleton className="h-xl w-full" />
            <Skeleton className="h-xl w-full" />
          </div>
        ) : documents.length === 0 ? (
          <div className="p-xxs">
            <EmptyState
              title={t('classDetail.documents.empty')}
              description={t('classDetail.documents.emptyDescription')}
            />
          </div>
        ) : (
          <ul className="flex flex-col">
            {documents.map(document => (
              <ContextFileRow
                key={document.id}
                document={document}
                onImport={onImport}
                onDelete={setPending}
              />
            ))}
          </ul>
        )}
      </SurfaceCard>

      <ConfirmDialog
        open={pending !== null}
        title={t('classDetail.documents.deleteConfirm.title')}
        body={t('classDetail.documents.deleteConfirm.body', {
          name: pending?.originalFileName ?? '',
        })}
        cancelLabel={t('classDetail.documents.confirmCancel')}
        confirmLabel={t('classDetail.documents.deleteConfirm.confirm')}
        tone="danger"
        onConfirm={handleConfirm}
        onOpenChange={open => {
          if (!open) setPending(null);
        }}
      />
    </>
  );
}
